import { useState, useMemo } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { getAuth, createUserWithEmailAndPassword } from 'firebase/auth'
import { Layout } from '../components/layout'
import { Input } from '../components/input'
import { Button } from '../components/button'
import { useSetAtom } from 'jotai'
import { toastAtom } from '../store/toast.store.js'
import { newsletterSubscribe, updateAirtable } from '../firestore'
import * as GtagService from '../utils/GtagService.js'
import { useCreateTeam } from '../entities/team/index.js'
import { getIsUserInvited } from '../entities/auth'
import { cn } from '../utils/index.js'

// Pragmatic, user-friendly email validation (no new libs)
const isValidEmail = (val) => {
  if (!val) return false
  const v = String(val).trim()
  // Simple pattern: non-space before and after @, a dot, and at least 2 TLD chars
  return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i.test(v)
}

export default function Signup() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [heardFrom, setHeardFrom] = useState('')
  const [signingUp, setSigningUp] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [emailUpdates, setEmailUpdates] = useState(false)
  const [teamName, setTeamName] = useState('My team')
  const addToast = useSetAtom(toastAtom)

  const passwordInputId = 'new-password'
  const confirmInputId = 'new-password-confirm'

  const [searchParams] = useSearchParams()
  const navigate = useNavigate()

  const { mutate: createTeam, isPending: isCreatingTeam } = useCreateTeam()

  const nextParam = searchParams.get('next')

  const isUserInvited = useMemo(() => getIsUserInvited(nextParam), [nextParam])

  const handleRedirect = async (teamId) => {
    // Update Airtable if user has set the heardFrom field
    if (heardFrom) {
      await updateAirtable({ payload: { 'Heard From': heardFrom } })
    }

    if (emailUpdates) {
      await newsletterSubscribe()
    }

    // Register conversion event
    GtagService.signup()

    if (teamId) return navigate('/dashboard/' + teamId)
    if (nextParam) return navigate(nextParam)
    return navigate('/dashboard')
  }

  const handleSignup = async (e) => {
    e.preventDefault()

    setSubmitted(true)

    if (!email || !password) return null

    // Validate email format after submit attempt
    const emailIsValid = isValidEmail(email)
    if (!emailIsValid) {
      const el = document.getElementById('email')
      if (el && 'focus' in el) {
        el.focus()
      }
      return null
    }

    // Validate password confirmation after submit attempt
    const passwordsMatch = password === confirmPassword
    if (!passwordsMatch) {
      // Focus the confirm password field for quick correction
      const el = document.getElementById(confirmInputId)
      if (el && 'focus' in el) {
        el.focus()
      }
      return null
    }

    setSigningUp(true)

    const auth = getAuth()

    await createUserWithEmailAndPassword(auth, email, password)
      .then(async ({ user }) => {
        if (isUserInvited) {
          return await handleRedirect()
        }

        createTeam(
          {
            uid: user.uid,
            name: teamName,
          },
          {
            async onSuccess(team) {
              await handleRedirect(team.id)
            },
          },
        )
      })
      .catch((error) => {
        const errorCode = error.code
        setSigningUp(false)
        switch (errorCode) {
          case 'auth/invalid-email':
            addToast({ title: 'Enter a valid email address', type: 'fail' })
            break
          case 'auth/email-already-in-use':
            addToast({ title: 'This user already exists', type: 'fail' })
            break

          case 'auth/weak-password':
            addToast({ title: 'Enter a more secure password', type: 'fail' })
            break

          default:
            addToast({
              title: 'An error occured',
              body: 'Please try again',
              type: 'fail',
            })
            break
        }
      })
  }
  return (
    <Layout title="Create an account" announcement>
      <form onSubmit={(e) => handleSignup(e)} className="flex flex-col gap-4">
        <Input
          id="email"
          label="Email"
          type="email"
          placeholder="me@email.com"
          autoFocus
          onChange={(change) => setEmail(change)}
          value={email}
          error={submitted && !!email && !isValidEmail(email)}
          errorText={
            submitted && !!email && !isValidEmail(email)
              ? 'Enter a valid email address'
              : undefined
          }
          InputProps={{
            name: 'email',
            autoComplete: 'email',
          }}
        />
        <Input
          id={passwordInputId}
          label="Password"
          type="password"
          placeholder="••••••••••"
          onChange={(change) => setPassword(change)}
          value={password}
          InputProps={{
            name: 'new-password',
            autoComplete: passwordInputId,
          }}
        />
        <Input
          id={confirmInputId}
          label="Confirm password"
          type="password"
          placeholder="••••••••••"
          onChange={(change) => setConfirmPassword(change)}
          value={confirmPassword}
          error={submitted && password !== confirmPassword}
          errorText={
            submitted && password !== confirmPassword
              ? 'Passwords do not match'
              : undefined
          }
          InputProps={{
            name: 'confirm-password',
            autoComplete: passwordInputId,
          }}
        />
        <hr className="border-0 border-b border-gray-700 border-solid" />
        <Input
          label="Where did you hear about Rundown Studio?"
          type="text"
          placeholder="Your answer"
          onChange={(change) => setHeardFrom(change)}
          value={heardFrom}
          InputProps={{
            'data-1p-ignore': 'true',
          }}
        />
        {!isUserInvited && (
          <Input
            type="text"
            label="Team name"
            placeholder="My new team..."
            required={!isUserInvited}
            value={teamName}
            onChange={setTeamName}
            InputProps={{
              'data-1p-ignore': 'true',
            }}
          />
        )}
        <label
          htmlFor="emailUpdates"
          className={cn(
            'p-2 mt-2 bg-gray-900 rounded-sm flex items-center gap-2 cursor-pointer hover:brightness-110',
          )}
        >
          <input
            id="emailUpdates"
            type="checkbox"
            className="focus:outline-hidden focus:ring-3"
            checked={emailUpdates}
            onChange={(e) => setEmailUpdates(e.target.checked)}
          />
          <span className="text-sm">Send me product updates. No spam.</span>
        </label>
        <p className="text-xs text-gray-500">
          By continuing, you are indicating that you accept our{' '}
          <a
            className="underline hover:text-gray-400"
            href="https://rundownstudio.app/terms-of-service"
            target="_blank"
            rel="noreferrer"
          >
            Terms of Service
          </a>{' '}
          and{' '}
          <a
            className="underline hover:text-gray-400"
            href="https://rundownstudio.app/privacy-policy"
            target="_blank"
            rel="noreferrer"
          >
            Privacy Policy
          </a>
          .
        </p>
        <Button
          disabled={!email || !password || signingUp}
          loading={signingUp || isCreatingTeam}
          text={
            <span>
              {signingUp || isCreatingTeam
                ? 'Creating your account...'
                : 'Create account'}
            </span>
          }
          onClick={handleSignup}
          type="submit"
        />
      </form>
      <p className="text-sm text-gray-200">
        Already have an account?{' '}
        <Link to="/login" className="text-blue-500 hover:underline">
          Log in here
        </Link>
      </p>
    </Layout>
  )
}
